// The Reports page's own words (B10 S15; plan D17; DC-210, DC-211). His report and measure names are GLOSSARY §14's
// definitions, verbatim; his filter names are BRD 7.2's sentence (G §14 *Filters*); column heads are his record words.
// The D0 label suites discover this module (a lane folder of src/views) and hold it to src/mc's rules: every quote is
// in the vendored glossary or design-system excerpts, every text sits inside its quote, nothing forbidden. Words the
// design system already defines (the KPI row, the bands, the clocks, the pipeline columns, Decline reasons, Candidates
// by stage, Save view, Export …) are imported, never redefined.
//
// A word his sources do not carry yet — *Not yet available*, *Unclassified (V1)*, a pager — is not drawn: T-8 says a
// new term enters the glossary first, and a label's quote must be a line of the vendored sources. Until DCR-B10-1
// lands, a figure waiting for its producer draws "—" (DS #18's empty value), and a reason the server sends draws as the
// server's own words (configuration data). A word of his that a glossary question still holds open (*Closure*, Q-E16)
// is drawn with status pending.

import { G6_COLUMN_LABELS } from "@/mc/g6";
import { METRIC_LABELS } from "@/mc/g3";
import { SURFACE_LABELS } from "@/mc/core";
import { COMMON_LABELS, HEALTH_BAND_LABELS, defineLabels, template, type LabelSource } from "@/mc/labels";

const his = <const S extends string>(text: S, source: LabelSource, quote: string) =>
  ({ text, source, quote, status: "his" }) as const;
/** His word that a glossary question still holds open (G §23, Q-E15, Q-E16, T-8): drawn, marked pending. */
const pending = <const S extends string>(text: S, source: LabelSource, quote: string) =>
  ({ text, source, quote, status: "pending" }) as const;

// ---- his definitions (G §14) ----------------------------------------------------------------------------------------

const PORTFOLIO =
  "| Portfolio (report) | \"Open reqs by recruiter/function/brand/location/level · On hold/cancelled/reopened/filled/archived · Days open, active days, hold days, health\". |";
const PIPELINE =
  "| Pipeline (report) | \"Candidates by stage · Qualified volume · Time in stage · Interview-to-offer · Source of hire/interview/offer · Internal vs external\". |";
const OFFER =
  "| Offer (report) | \"Extended/accepted/declined · Acceptance/negotiation rate · Decline reasons · Acceptance-to-HRIS/start\". |";
const EXPERIENCE =
  "| Candidate experience (report) | \"First contact · Days between stages · Days to disposition · Withdrawal stage/reason · Undispositioned/alternate closure\". |";
const HM_RESPONSIVENESS =
  "| HM responsiveness (report) | \"Resume review · Candidate selection · Scorecard/feedback · Debrief participation · Offer approval\". |";
const RECRUITER_OPERATIONS =
  "| Recruiter operations (report) | \"Assignment-to-intake/first candidate · Follow-up · Offer creation · Closure · Own metrics; TA Manager team view\". |";
const COORDINATOR_OPERATIONS =
  "| Coordinator operations (report) | \"Time to schedule each meeting type · Reschedules · Panel decline/nonresponse · Availability/conflict delays · Debrief SLA\". |";
const EXECUTIVE = "| Executive dashboard | Register row \"CR-041 Executive dashboard\". |";
const FILTERS =
  "\"Support date, recruiter, coordinator, HRBP, HM, function, department, brand, job family, area of interest, level, location, strategy, new/replacement, status, phase, health, days open, time in stage, source, internal/external, offer status, standardized reasons, saved views, grouping, sorting, security-trimmed drill-down and exports.\"";
const ACCEPT_HRIS = "| Acceptance to HRIS | \"Acceptance to HRIS = accepted to HRIS hire.\" |";
const ACCEPT_START = "| Acceptance to Start | \"Acceptance to Start = accepted to start date.\" |";

/** The tabs: his seven reports and the Executive dashboard, keyed by family (`report.family.<key>`). */
export const REPORT_FAMILY_LABELS = /* @__PURE__ */ defineLabels({
  portfolio: his("Portfolio"),
  executive: his("Executive dashboard"),
  pipeline: his("Pipeline"),
  offer: his("Offer"),
  candidate_experience: his("Candidate experience"),
  hm_responsiveness: his("HM responsiveness"),
  recruiter_operations: his("Recruiter operations"),
  coordinator_operations: his("Coordinator operations"),
});

/** His measure words, one per item of his report definitions. */
export const REPORT_MEASURE_WORDS = /* @__PURE__ */ defineLabels({
  openReqs: his("Open reqs"),
  onHold: his("On hold"),
  cancelled: his("Cancelled"),
  reopened: his("Reopened"),
  filled: his("Filled"),
  archived: his("Archived"),
  statusSeries: his("On hold/cancelled/reopened/filled/archived"),
  daysOpen: his("Days open"),
  activeDays: his("Active days"),
  holdDays: his("Hold days"),
  health: his("Health"),
  qualifiedVolume: his("Qualified volume"),
  timeInStage: his("Time in stage"),
  interviewToOffer: his("Interview-to-offer"),
  sourceOfHire: his("Source of hire"),
  interview: his("interview"),
  offer: his("offer"),
  internal: his("Internal"),
  external: his("External"),
  extended: his("Extended"),
  accepted: his("Accepted"),
  declined: his("Declined"),
  offerSeries: his("Extended/accepted/declined"),
  acceptance: his("Acceptance"),
  negotiation: his("Negotiation"),
  acceptanceToHris: his("Acceptance to HRIS"),
  acceptanceToStart: his("Acceptance to Start"),
  firstContact: his("First contact"),
  daysBetweenStages: his("Days between stages"),
  daysToDisposition: his("Days to disposition"),
  withdrawalStageReason: his("Withdrawal stage/reason"),
  undispositioned: his("Undispositioned"),
  alternateClosure: his("Alternate closure"),
  resumeReview: his("Resume review"),
  candidateSelection: his("Candidate selection"),
  scorecardFeedback: his("Scorecard/feedback"),
  debriefParticipation: his("Debrief participation"),
  offerApproval: his("Offer approval"),
  assignmentToIntake: his("Assignment-to-intake"),
  firstCandidate: his("first candidate"),
  followUp: his("Follow-up"),
  offerCreation: his("Offer creation"),
  // His CR-044 item, and also the short closure-tab label Q-E16 asks him about ("… or a short *Closure*?"): the
  // design system marks that text pending until he answers, so his measure word is drawn pending too.
  closure: pending("Closure"),
  ownMetrics: his("Own metrics"),
  teamView: his("Team view"),
  timeToSchedule: his("Time to schedule each meeting type"),
  meetingType: his("Meeting type"),
  reschedules: his("Reschedules"),
  debriefSla: his("Debrief SLA"),
});

/** His items that name two figures with one word ("Source of hire/interview/offer", "Acceptance/negotiation rate",
 *  "Assignment-to-intake/first candidate"): each figure is his word filled into the shared part of his phrase. */
export const REPORT_MEASURE_TEMPLATES = {
  sourceOf: /* @__PURE__ */ template(his("Source of {what}")),
  rate: /* @__PURE__ */ template(his("{what} rate")),
  assignmentTo: /* @__PURE__ */ template(his("Assignment-to-{what}")),
} as const;

/** BRD 7.2's filter names, keyed by dimension (`report.dimension.<key>`), in his order. */
export const REPORT_DIMENSION_LABELS = /* @__PURE__ */ defineLabels({
  date: his("Date"),
  recruiter: his("Recruiter"),
  coordinator: his("Coordinator"),
  hrbp: his("HRBP"),
  hm: his("HM"),
  function: his("Function"),
  department: his("Department"),
  brand: his("Brand"),
  job_family: his("Job family"),
  area_of_interest: his("Area of interest"),
  level: his("Level"),
  location: his("Location"),
  strategy: his("Strategy"),
  new_replacement: his("New/replacement"),
  status: his("Status"),
  phase: his("Phase"),
  health: his("Health"),
  days_open: his("Days open"),
  time_in_stage: his("Time in stage"),
  source: his("Source"),
  internal_external: his("Internal/external"),
  offer_status: his("Offer status"),
  reasons: his("Standardized reasons"),
  decision_at: his("Decision timestamp"),
  meeting_type: his("Meeting type"),
});

/** The page's own words: the filter picker, the drill-down card, the drill-down's column heads, the export format and
 *  the rule the export banner states. */
export const REPORT_PAGE_LABELS = /* @__PURE__ */ defineLabels({
  filters: his("Filters"),
  drillDown: his("Drill-down"),
  csv: his("CSV"),
  restrictedFieldsDoNotLeak: his("Restricted fields do not leak."),
  reqId: his("REQ ID"),
  requisition: his("Requisition"),
  configurationVersion: his("Configuration version"),
  recommendedHiringRange: his("Recommended hiring range"),
  stage: his("Stage"),
  withdrawalReason: his(
    "Withdrawal reason"
),
});

// ---- resolution ------------------------------------------------------------------------------------------------------

const W = REPORT_MEASURE_WORDS;
const T = REPORT_MEASURE_TEMPLATES;

/** The DC-31 column of a pipeline stage measure (`stage_<column>`), in the design system's own keys. */
const STAGE_COLUMN: Readonly<Record<string, keyof typeof G6_COLUMN_LABELS>> = {
  sourced: "sourced",
  qualified: "qualified",
  in_hm_review: "inHmReview",
  recruiter_screen: "recruiterScreen",
  hm_interview: "hmInterview",
  final_interview_loop: "finalInterviewLoop",
  offer: "offer",
};

/** Every measure the families register (`report.measure.<family>.<key>`), in his words. */
export const MEASURE_LABELS: Readonly<Record<string, string>> = {
  // Portfolio (CR-041)
  "portfolio.open": W.openReqs,
  "portfolio.on_hold": W.onHold,
  "portfolio.cancelled": W.cancelled,
  "portfolio.reopened": W.reopened,
  "portfolio.filled": W.filled,
  "portfolio.archived": W.archived,
  "portfolio.days_open": W.daysOpen,
  "portfolio.health": W.health,
  // Executive dashboard (DC-51: his five, Time to Fill beneath)
  "executive.open": METRIC_LABELS.openRequisitions,
  "executive.active_candidates": METRIC_LABELS.activeCandidates,
  "executive.time_to_acceptance": METRIC_LABELS.timeToAcceptance,
  "executive.time_to_fill": METRIC_LABELS.timeToFill,
  "executive.offers_accepted": METRIC_LABELS.offersAccepted,
  "executive.health": METRIC_LABELS.reqHealth,
  "executive.health_green": HEALTH_BAND_LABELS.green,
  "executive.health_yellow": HEALTH_BAND_LABELS.yellow,
  "executive.health_red": HEALTH_BAND_LABELS.red,
  // Pipeline (CR-045 family, BRD ch. 7)
  "pipeline.candidates_by_stage": SURFACE_LABELS.candidatesByStage,
  ...Object.fromEntries(Object.entries(STAGE_COLUMN).map(([column, key]) => [`pipeline.stage_${column}`, G6_COLUMN_LABELS[key]])),
  "pipeline.qualified_volume": W.qualifiedVolume,
  "pipeline.time_in_stage": W.timeInStage,
  "pipeline.interview_to_offer": W.interviewToOffer,
  "pipeline.source_of_hire": W.sourceOfHire,
  "pipeline.source_of_interview": T.sourceOf({ what: W.interview }),
  "pipeline.source_of_offer": T.sourceOf({ what: W.offer }),
  "pipeline.internal": W.internal,
  "pipeline.external": W.external,
  // Offer (CR-039)
  "offer.extended": W.extended,
  "offer.accepted": W.accepted,
  "offer.declined": W.declined,
  "offer.acceptance_rate": T.rate({ what: W.acceptance }),
  "offer.negotiation_rate": T.rate({ what: W.negotiation }),
  "offer.decline_reasons": SURFACE_LABELS.declineReasons,
  "offer.acceptance_to_hris": W.acceptanceToHris,
  "offer.acceptance_to_start": W.acceptanceToStart,
  // Candidate experience (CR-045)
  "candidate_experience.first_contact": W.firstContact,
  "candidate_experience.days_between_stages": W.daysBetweenStages,
  "candidate_experience.days_to_disposition": W.daysToDisposition,
  "candidate_experience.withdrawals": W.withdrawalStageReason,
  "candidate_experience.undispositioned": W.undispositioned,
  "candidate_experience.alternate_closure": W.alternateClosure,
  // HM responsiveness (CR-042)
  "hm_responsiveness.resume_review": W.resumeReview,
  "hm_responsiveness.candidate_selection": W.candidateSelection,
  "hm_responsiveness.scorecard_feedback": W.scorecardFeedback,
  "hm_responsiveness.debrief_participation": W.debriefParticipation,
  "hm_responsiveness.offer_approval": W.offerApproval,
  // Recruiter operations (CR-044)
  "recruiter_operations.assignment_to_intake": W.assignmentToIntake,
  "recruiter_operations.assignment_to_first_candidate": T.assignmentTo({ what: W.firstCandidate }),
  "recruiter_operations.follow_up": W.followUp,
  "recruiter_operations.offer_creation": W.offerCreation,
  "recruiter_operations.closure": W.closure,
  // Coordinator operations (CR-082, CR-090) — S09's keys; any other key it registers resolves through his phrase below
  "coordinator_operations.time_to_schedule": W.timeToSchedule,
  "coordinator_operations.reschedules": W.reschedules,
  "coordinator_operations.panel_decline_nonresponse": METRIC_LABELS.panelDeclineNonresponse,
  "coordinator_operations.availability_conflict_delays": METRIC_LABELS.availabilityConflictDelays,
  "coordinator_operations.debrief_sla": W.debriefSla,
  "coordinator_operations.open_requests": METRIC_LABELS.toSchedule,
};

/** Each family's items in his order, for a measure key the table above does not name (a family slice that lands
 *  after this one): the item whose leading words the key spells. */
const FAMILY_ITEMS: Readonly<Record<string, readonly string[]>> = {
  portfolio: [W.openReqs, W.onHold, W.cancelled, W.reopened, W.filled, W.archived, W.daysOpen, W.activeDays, W.holdDays, W.health],
  executive: [
    METRIC_LABELS.openRequisitions, METRIC_LABELS.activeCandidates, METRIC_LABELS.timeToAcceptance, METRIC_LABELS.timeToFill,
    METRIC_LABELS.offersAccepted, METRIC_LABELS.reqHealth,
  ],
  pipeline: [SURFACE_LABELS.candidatesByStage, W.qualifiedVolume, W.timeInStage, W.interviewToOffer, W.sourceOfHire, W.internal, W.external],
  offer: [W.extended, W.accepted, W.declined, SURFACE_LABELS.declineReasons, W.acceptanceToHris, W.acceptanceToStart],
  candidate_experience: [W.firstContact, W.daysBetweenStages, W.daysToDisposition, W.withdrawalStageReason, W.undispositioned, W.alternateClosure],
  hm_responsiveness: [W.resumeReview, W.candidateSelection, W.scorecardFeedback, W.debriefParticipation, W.offerApproval],
  recruiter_operations: [W.assignmentToIntake, W.followUp, W.offerCreation, W.closure],
  coordinator_operations: [
    W.timeToSchedule, W.reschedules, METRIC_LABELS.panelDeclineNonresponse, METRIC_LABELS.availabilityConflictDelays, W.debriefSla,
  ],
};

const words = (text: string): string[] => text.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

/** The item of his phrase whose words the key's words begin (`panel_decline` → *Panel decline/nonresponse*), else the
 *  one item that holds all of them in order (`panel_nonresponse` → the same); none when that is not one item. */
function byPhrase(family: string, key: string): string | undefined {
  const want = words(key);
  if (want.length === 0) return undefined;
  const items = FAMILY_ITEMS[family] ?? [];
  const prefix = items.find((item) => {
    const have = words(item);
    return want.length <= have.length && want.every((word, i) => have[i] === word);
  });
  if (prefix) return prefix;
  const inOrder = items.filter((item) => {
    let at = 0;
    const have = words(item);
    for (const word of want) {
      at = have.indexOf(word, at);
      if (at < 0) return false;
      at += 1;
    }
    return true;
  });
  return inOrder.length === 1 ? inOrder[0] : undefined;
}

/** `report.measure.<family>.<key>` → `{family, key}`; anything else → null. */
export function splitMeasureKey(labelKey: string): { family: string; key: string } | null {
  const m = /^report\.measure\.([a-z][a-z_]*)\.([a-z][a-z0-9_]*)$/.exec(labelKey);
  return m && m[1] && m[2] ? { family: m[1], key: m[2] } : null;
}

/** A measure's words from its label key; null only for a key no family item spells (the test names every one). */
export function measureLabel(labelKey: string): string | null {
  const parts = splitMeasureKey(labelKey);
  if (!parts) return null;
  return MEASURE_LABELS[`${parts.family}.${parts.key}`] ?? byPhrase(parts.family, parts.key) ?? null;
}

/** A family's tab word from its key or its label key (`report.family.<key>`); null for a family not yet named. */
export function familyLabel(keyOrLabelKey: string): string | null {
  const key = keyOrLabelKey.replace(/^report\.family\./, "");
  return (REPORT_FAMILY_LABELS as Readonly<Record<string, string>>)[key] ?? null;
}

/** A dimension's filter name: his 7.2 word, else the word the server registered it with (his, verbatim). */
export function dimensionLabel(key: string, serverWords?: string | null): string {
  const mine = (REPORT_DIMENSION_LABELS as Readonly<Record<string, string>>)[key];
  if (mine) return mine;
  const w = (serverWords ?? "").trim();
  return w ? w.charAt(0).toUpperCase() + w.slice(1) : key;
}

/** The drill-down's sort keys (`report.options` `sorts`) in his words; a key without one is not offered. */
export const SORT_LABELS: Readonly<Record<string, string>> = {
  id: REPORT_PAGE_LABELS.reqId,
  title: REPORT_PAGE_LABELS.requisition,
  requisition: REPORT_PAGE_LABELS.reqId,
  level: REPORT_DIMENSION_LABELS.level,
  department: REPORT_DIMENSION_LABELS.department,
  status: REPORT_DIMENSION_LABELS.status,
  days_open: W.daysOpen,
  health: W.health,
  stage: REPORT_PAGE_LABELS.stage,
  time_in_stage: W.timeInStage,
  decision_at: REPORT_DIMENSION_LABELS.decision_at,
};

/** The application families' `id` sort orders candidate rows by the application, which his words name by the
 *  candidate: *Candidate ID*. */
export const APPLICATION_SORT_LABELS: Readonly<Record<string, string>> = { ...SORT_LABELS, id: COMMON_LABELS.candidateId };
